import { Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import {
  CornerFixingHoles,
  KickPlateFixingHoles,
  kickPlateFeatureTypeOptions,
  otherFeatureTypeOptions,
  plateTypeOptions,
} from 'metalworks-domain'
import { type $FeatureSetting } from 'metalworks-domain/src/__generated/admin-enhanced-model.js'
import { createAdminRecipe } from 'metalworks-domain/src/__generated/admin-implementation.js'
import { AnchorPointPosition, FeatureSetting } from 'metalworks-domain/src/__generated/admin-model/index.js'
import { useCallback, useMemo } from 'react'

import { DeleteEntityMenuItem, EditEntityMenuItem, EntityRow } from '@salsita-ai/platform-project-sdk/admin'
import { AttributeToggleSelector, GenericDimensionInput } from '@salsita-ai/platform-project-sdk/react'
import { PhxIcon, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { adminDomainHooks } from '../systems/admin-hooks.js'

export const FeatureSettingsPageActions = () => {
  const { rootEntityId } = adminDomainHooks.useDomain()

  const featureSettings = adminDomainHooks.useStoreSelector(e => {
    return e.state.entities.filter(FeatureSetting.is)
  }, [])

  const getNewSetting = useCallback(() => {
    for (const { value: plateType } of plateTypeOptions) {
      const featureTypeOptions = plateType === 'KickPlate' ? kickPlateFeatureTypeOptions : otherFeatureTypeOptions

      for (const { value: featureType } of featureTypeOptions) {
        const exists = featureSettings.some(
          entity => entity.plateType === plateType && entity.featureType === featureType
        )

        if (!exists) {
          return {
            plateType,
            featureType,
          }
        }
      }
    }

    return null
  }, [featureSettings])

  const recipes = useMemo(() => {
    const newSetting = getNewSetting()

    if (!newSetting) {
      return []
    }

    return [
      createAdminRecipe({
        type: 'FeatureSetting',
        plateType: newSetting.plateType,
        featureType: newSetting.featureType,
        anchorPointPosition: AnchorPointPosition.center,
        offsetX: 0,
        offsetY: 0,
      }),
    ]
  }, [getNewSetting])

  const { disabled, create } = adminDomainHooks.useCookedRecipes(recipes, rootEntityId)

  return (
    <Button
      color="primary"
      variant="contained"
      startIcon={<PhxIcon name="PlusBold" />}
      onClick={create}
      disabled={disabled}
    >
      Add Feature
    </Button>
  )
}

interface SidebarProps {
  id: $FeatureSetting['id']
}

export const FeatureSettingSidebar = (props: SidebarProps) => {
  const { id } = props

  const state = adminDomainHooks.useEntityState(id)

  const canConfigurePosition = useMemo(
    () =>
      state.featureType
        ? !(KickPlateFixingHoles.typeSet as Set<string>).has(state.featureType) &&
          !(CornerFixingHoles.typeSet as Set<string>).has(state.featureType)
        : true,
    [state.featureType]
  )

  return (
    <PropertyContainer.Group spacing="medium">
      <PropertyContainer.Item title="Plate Type">
        <AttributeToggleSelector entityId={id} instanceName="plateType" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Feature Type">
        <AttributeToggleSelector entityId={id} instanceName="featureType" />
      </PropertyContainer.Item>
      {canConfigurePosition && (
        <PropertyContainer.Item title="Anchor Point">
          <AttributeToggleSelector entityId={id} instanceName="anchorPoint" />
        </PropertyContainer.Item>
      )}
      <PropertyContainer.Item title="Offset X">
        <GenericDimensionInput entityId={id} instanceName="offsetX" />
      </PropertyContainer.Item>
      <PropertyContainer.Item title="Offset Y">
        <GenericDimensionInput entityId={id} instanceName="offsetY" />
      </PropertyContainer.Item>
    </PropertyContainer.Group>
  )
}

interface FeatureSettingActionsProps {
  id: $FeatureSetting['id']
  onClose: () => void
}

const FeatureSettingsActions = (props: FeatureSettingActionsProps) => {
  const { id, onClose } = props

  return (
    <>
      <EditEntityMenuItem id={id} onClose={onClose} title="Edit Feature Setting" bold />
      <Divider />
      <DeleteEntityMenuItem id={id} onClose={onClose} />
    </>
  )
}

// TODO: this should be an expression
const getFeatureSettingName = (entity: FeatureSetting) => `${entity.plateType} / ${entity.featureType}`

interface FeatureSettingRowProps {
  id: $FeatureSetting['id']
}

const FeatureSettingRow = (props: FeatureSettingRowProps) => {
  const { id } = props

  const state = adminDomainHooks.useEntityState(id)
  const select = adminDomainHooks.useSelectEntity()

  const enter = useCallback(() => {
    select(id)
  }, [select, id])

  return (
    <EntityRow.Root
      key={state.id}
      id={state.id}
      onClick={enter}
      name={getFeatureSettingName(state)}
      showThumbnail={false}
      Actions={FeatureSettingsActions}
    />
  )
}

export const FeatureSettingsPage = () => {
  const featureSettings = adminDomainHooks.useStoreSelector(e => {
    return e.state.entities.filter(FeatureSetting.is)
  }, [])

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell sx={{ width: 62 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {featureSettings.map(entity => (
            <FeatureSettingRow key={entity.id} id={entity.id} />
          ))}

          {featureSettings.length === 0 && <EntityRow.EmptyRow title="No feature settings found" span={5} />}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
